import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { showSuccessToast, showErrorToast } from '@/utils/toastUtils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MissionStats {
  activeMissions: number;
  totalParticipants: number;
  completed: number;
  pendingShipments: number;
}

interface MissionData {
  id: string;
  title: string;
  description: string;
  total_days: number;
  started: number;
  completed: number;
  completionRate: number;
}

interface PendingShipment {
  id: string;
  user_id: string;
  full_name: string;
  phone: string;
  address: string;
  number: string;
  complement?: string;
  zip_code: string;
  submitted_at: string;
  username?: string;
  completed_at?: string;
  daysAgo: number;
}

export const useAdminMissions = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<MissionStats>({
    activeMissions: 0,
    totalParticipants: 0,
    completed: 0,
    pendingShipments: 0,
  });
  const [pendingShipments, setPendingShipments] = useState<PendingShipment[]>([]);
  const [mission, setMission] = useState<MissionData | null>(null);

  const fetchData = async () => {
    try {
      // Fetch active mission
      const { data: missionData, error: missionError } = await supabase
        .from('missions')
        .select('*')
        .eq('is_active', true)
        .eq('title', 'Jornada de Lançamento Interlaced')
        .single();

      if (missionError) throw missionError;

      // Fetch stats
      const { data: userMissionsData, error: statsError } = await supabase
        .from('user_missions')
        .select('*')
        .eq('mission_id', missionData.id);

      if (statsError) throw statsError;

      const completed = userMissionsData.filter(um => um.status === 'completed').length;
      const completionRate = userMissionsData.length > 0 
        ? Math.round((completed / userMissionsData.length) * 100)
        : 0;

      setMission({
        id: missionData.id,
        title: missionData.title,
        description: missionData.description || '',
        total_days: missionData.total_days,
        started: userMissionsData.length,
        completed,
        completionRate,
      });

      setStats({
        activeMissions: 1,
        totalParticipants: userMissionsData.length,
        completed,
        pendingShipments: 0, // Will be updated below
      });

      // Fetch pending shipments with user data
      const { data: shipmentsData, error: shipmentsError } = await supabase
        .from('reward_shipments')
        .select(`
          *,
          profiles!reward_shipments_user_id_fkey (username)
        `)
        .eq('mission_id', missionData.id)
        .eq('sent', false)
        .order('submitted_at', { ascending: false });

      if (shipmentsError) throw shipmentsError;

      // Get user mission data for completion dates
      const userIds = shipmentsData?.map(s => s.user_id) || [];
      const { data: userMissionData, error: userMissionError } = await supabase
        .from('user_missions')
        .select('user_id, completed_at')
        .eq('mission_id', missionData.id)
        .in('user_id', userIds);

      if (userMissionError) throw userMissionError;

      const userMissionMap = new Map<string, string | null>(
        userMissionData?.map(um => [um.user_id, um.completed_at]) || []
      );

      const formattedShipments: PendingShipment[] = (shipmentsData || []).map(shipment => {
        const completedAt = userMissionMap.get(shipment.user_id);
        const completedAtStr = completedAt ?? undefined;
        const daysAgo = completedAtStr 
          ? Math.ceil((Date.now() - new Date(completedAtStr).getTime()) / (1000 * 60 * 60 * 24))
          : 0;

        return {
          id: shipment.id,
          user_id: shipment.user_id,
          full_name: shipment.full_name,
          phone: shipment.phone,
          address: shipment.address,
          number: shipment.number,
          complement: shipment.complement || undefined,
          zip_code: shipment.zip_code,
          submitted_at: shipment.submitted_at,
          username: (shipment.profiles as { username?: string } | null)?.username || 'Usuário',
          completed_at: completedAtStr,
          daysAgo,
        };
      });

      setPendingShipments(formattedShipments);
      setStats(prev => ({ ...prev, pendingShipments: formattedShipments.length }));

    } catch (error) {
      debugLogger.error('[useAdminMissions] Error fetching data:', error);
      showErrorToast({
        title: 'Erro ao carregar dados',
        description: 'Não foi possível carregar as informações das missões.'
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsSent = async (shipmentId: string) => {
    try {
      const { error } = await supabase
        .from('reward_shipments')
        .update({ sent: true })
        .eq('id', shipmentId);

      if (error) throw error;

      showSuccessToast({
        title: 'Marcado como enviado!',
        description: 'A recompensa foi marcada como enviada.'
      });

      await fetchData();

    } catch (error) {
      debugLogger.error('[useAdminMissions] Error marking as sent:', error);
      showErrorToast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível marcar como enviado.'
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    loading,
    stats,
    mission,
    pendingShipments,
    markAsSent,
    refetch: fetchData,
  };
};
